import { FluentProvider } from "@fluentui/react-components";
import { HashRouter as Router, Route, Routes } from "react-router-dom";
import React, { useState, useEffect, useContext } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  TeamcenterError,
  TeamsFxContext,
  useTeamsContext,
  DataPrivacyInitial,
  Authentication,
  TeamcenterAuthPopup,
  useTCTeamsContext,
  TCTeamsFxContext,
  PolarionTeamsContext,
  usePolarionTeamsContext,
  logger,
} from "@siemensplm/common_utils";
import "@siemensplm/common_utils/dist/css/authentication/auth.css";
import "@siemensplm/common_utils/dist/css/common/style.css";
import Privacy from "./Privacy";
import TermsOfUse from "./TermsOfUse";
import { CapabilityHandler } from "./CapabilityHandler";
import Apps from "./Apps";
import { Constants } from "../common/constants";
import {
  useCapabilitiesContext,
  CapabilitiesProvider,
} from "../context/CapabilitiesContext";
import { CapabilityRouter } from "./CapabilityRouter";

// Create a QueryClient instance
const queryClient = new QueryClient();

/**
 * The main app which handles the initialization and routing
 * of the app and selected capability.
 */
function MainApp() {
  const teamsContext = useTeamsContext();
  const tcTeamsContext = useTCTeamsContext(teamsContext, queryClient);
  const polarionTeamsContext = usePolarionTeamsContext(
    teamsContext,
    queryClient
  );
  const { activeCapability, loadCapability } = useCapabilitiesContext();
  const [capabilityName, setCapabilityName] = useState<string | null>(null);
  const [capabilityId, setCapabilityId] = useState<string | null>(null);
  const [capabilityDomain, setCapabilityDomain] = useState<string | null>(null);
  const [hash, setHash] = useState<string>(window.location.hash);

  // Listen for hash changes to update the capabilityName, capabilityId, and capabilityDomain
  // This is required to open any capabilities dialog thrrough URL - Example: TeamcenterAuthPopup
  useEffect(() => {
    const loadCapabilityAsync = async () => {
      if (
        capabilityName &&
        capabilityId &&
        capabilityDomain &&
        typeof loadCapability === "function"
      ) {
        const isCapabilityLoaded = await loadCapability(
          capabilityName,
          capabilityId,
          capabilityDomain
        );
        if (isCapabilityLoaded) {
          const value = {
            capabilityName: capabilityName,
            capabilityId: capabilityId,
            capabilityDomain: capabilityDomain,
          };
          const capability = JSON.stringify(value);
          localStorage.setItem(Constants.activeCapability, capability);
        }
      }
    };
    loadCapabilityAsync();
  }, [capabilityId, capabilityName]);

  useEffect(() => {
    logger.setActiveCapability(
      activeCapability.capabilityDetails.capabilityName || ""
    );
    logger.setServiceName("FrontEnd");
  }, [activeCapability]);

  useEffect(() => {
    const queryStringStart = hash.indexOf("?");
    // Extract query parameters from the hash
    if (queryStringStart > -1) {
      const queryString = hash.substring(queryStringStart + 1);
      const params = new URLSearchParams(queryString);
      setCapabilityName(params.get("capabilityName"));
      setCapabilityId(params.get("capabilityId"));
      setCapabilityDomain(params.get("capabilityDomain"));
    }
  }, [hash]);

  useEffect(() => {
    const initTeams = async () => {
      if (
        !tcTeamsContext.teamcenter.isAuthenticated ||
        !teamsContext?.context?.page?.subPageId ||
        teamsContext.context.page.frameContext === "task"
      )
        return;

      const currentSubPageId = teamsContext.context.page.subPageId;
      const parts = currentSubPageId.split("|");

      if (parts && parts.length > 0) {
        const encodedRoutePart = parts.find((part) =>
          part.startsWith("encodedRoute=")
        );
        if (encodedRoutePart) {
          const value = encodedRoutePart.substring("encodedRoute=".length);
          if (value) {
            const decodedRoute = `${decodeURIComponent(value)}&redirect=/apps`;
            setTimeout(() => {
              window.location.hash = decodedRoute;
            }, 100);
          }
        }
      }
    };
    initTeams();
  }, [teamsContext?.context?.page?.subPageId]);

  return (
    <TeamsFxContext.Provider value={teamsContext}>
      <TCTeamsFxContext.Provider value={tcTeamsContext}>
        <PolarionTeamsContext.Provider value={polarionTeamsContext}>
          <QueryClientProvider client={queryClient}>
            <activeCapability.capability>
              <FluentProvider theme={teamsContext.theme}>
                <Router>
                  <Routes>
                    {/* Dynamically create routes based on the routesConfig object */}
                    <Route path="/*" element={<CapabilityRouter />} />
                    <Route
                      path="/teamcenterauthpopup"
                      element={<TeamcenterAuthPopup />}
                    />
                    <Route
                      path="/apps"
                      element={
                        <Authentication>
                          <Apps />
                        </Authentication>
                      }
                    />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/termsofuse" element={<TermsOfUse />} />
                    <Route
                      path="/callback"
                      element={
                        <Authentication>
                          <CapabilityHandler />
                        </Authentication>
                      }
                    />
                    <Route
                      path="/dataprivacyinitial"
                      element={
                        <Authentication>
                          <DataPrivacyInitial />
                        </Authentication>
                      }
                    />
                  </Routes>
                </Router>
                <TeamcenterError />
              </FluentProvider>
            </activeCapability.capability>
          </QueryClientProvider>
        </PolarionTeamsContext.Provider>
      </TCTeamsFxContext.Provider>
    </TeamsFxContext.Provider>
  );
}

export default function App() {
  return (
    <CapabilitiesProvider>
      <MainApp />
    </CapabilitiesProvider>
  );
}
